import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getDotColorFromRating, parseTranslationWithPlaceholders } from 'commons/src/commonFunctions';
import SensorStatusDot from 'commons/src/components/sensors/SensorStatusDot';
import { TRANSLATION_PLACEHOLDERS } from 'commons/src/constants';
import { Rating, SensorCardMode } from 'commons/src/models/commonEnums';
import { formatRating, getStatusColor } from 'commons/src/sensorRatingUtils';
import styles from '../SensorsView.module.scss';

type SensorCardSummaryMode = {
    mode: SensorCardMode.Summary;
    title: string;
    value: number;
    unit: string;
    rating: Rating;
};

type SensorCardDetailMode = {
    mode: SensorCardMode.Detail;
    count: number;
    label: string;
    rating: Rating;
    peakRange: { min: number; max: number };
    unit: string;
};

export type SensorCardProps = SensorCardSummaryMode | SensorCardDetailMode;

export const SensorCard: React.FC<SensorCardProps> = props => {
    const { t: txt } = useTranslation();
    const { mode } = props;

    const detailProps = mode === SensorCardMode.Detail ? props : null;
    const peakRangeText = useMemo(() => {
        if (detailProps === null) return null;
        const translation = txt('InsightIaq.PeaksRangingFrom');
        return parseTranslationWithPlaceholders(
            translation,
            {
                [TRANSLATION_PLACEHOLDERS.MIN]: detailProps.peakRange.min,
                [TRANSLATION_PLACEHOLDERS.MAX]: detailProps.peakRange.max,
                [TRANSLATION_PLACEHOLDERS.UNIT]: detailProps.unit,
            },
            new Set([TRANSLATION_PLACEHOLDERS.MIN, TRANSLATION_PLACEHOLDERS.MAX])
        );
    }, [txt, detailProps]);

    if (mode === SensorCardMode.Summary) {
        const { title, value, unit, rating } = props;
        const statusColor = getStatusColor(rating);

        return (
            <div className={styles.card}>
                <h4 className={styles.summaryCardTitle}>{title}</h4>
                <div className={styles.summaryCardValue}>
                    <span className={styles.value}>{value.toFixed(0)}</span>
                    <span className={styles.unit}>{unit}</span>
                </div>
                <div className={styles.summaryCardStatus}>
                    <span className={styles.statusText} style={{ color: statusColor }}>
                        {formatRating(rating)}
                    </span>
                </div>
            </div>
        );
    }

    const { count, label, rating } = props;
    const dotColor = getDotColorFromRating(rating);

    return (
        <div className={styles.card}>
            <div className={styles.detailCardHeader}>
                <SensorStatusDot color={dotColor} />
                <span className={styles.detailCount}>{txt('InsightIaq.SpacesCount', { count })}</span>
            </div>
            <div className={styles.detailLabel}>{label}</div>
            <div className={styles.detailPeakRange}>{peakRangeText}</div>
        </div>
    );
};
